import m from 'mithril'
import mapboxgl from 'mapbox-gl'
import { v4 as uuidv4 } from 'uuid'
import {diff} from 'deep-object-diff'
// import rfdc from 'rfdc'
import clone from 'lodash/cloneDeep' // Temporary, until rfdc Buffer issue is resolved. Possibly related: https://github.com/davidmarkclements/rfdc/issues/21
import * as Comlink from "comlink"
// import clone from 'lodash/cloneDeep' // Temporary, until rfdc Buffer issue is resolved. Possibly related: https://github.com/davidmarkclements/rfdc/issues/21

import 'mapbox-gl/dist/mapbox-gl.css'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import './styles.scss'

// const clone = rfdc({ proto: false, circles: false })


const worker = new Worker(new URL("./diff-worker.js", import.meta.url), { type: "module" })
const diff_worker = Comlink.wrap(worker) 



/********************************************************************************************************************************
 * MapboxComponent:
 * Loads mapbox and renders it within a div that is reused by mithril
/********************************************************************************************************************************/

// Set the access token to authorize access public mapbox
mapboxgl.accessToken = 'pk.eyJ1IjoiZmxvd2xhYnMiLCJhIjoiY2tsdHA4bnBzMXlyNDJvcW1pb3hnNmlyaCJ9.cRqePCdy18OG0htmCvpsnw';


// Create the reusable element to render the mapbox map within
const map_element = document.createElement("div")
map_element.setAttribute("class", "map");


const request_interceptors = {}

// Tracks the spec currently applied to each Mapbox source/layer, keyed by id.
// Survives Mithril vnode lifecycles (unlike vnode.state) so the persist-reuse
// branches in Source.oninit / Layer.oninit can detect which fields actually
// changed since the last sync — and skip setters that would otherwise
// invalidate the tile cache or force redraws on every page navigation.
const last_applied_source_specs = new Map()
const last_applied_layer_specs = new Map()

function arraysShallowEqual(a, b){
	if(a === b) return true
	if(!Array.isArray(a) || !Array.isArray(b)) return false
	if(a.length !== b.length) return false
	for(let i = 0; i < a.length; i++){
		if(a[i] !== b[i]) return false
	}
	return true
}


// Instantiate the mapbox object and export it
export const map = new mapboxgl.Map({
    container: map_element,
    center: [-78.638179,35.779590],
    zoom: 10,
	maxPitch: 85,
	doubleClickZoom: false,
	options: {
		preserveDrawingBuffer: true,
	},
    style: 'mapbox://styles/flowlabs/cl3yugqyo001m15qsm67whcjr',
	transformRequest: (url, resourceType, a,b,c,d,e) => {

		Object.values(request_interceptors).forEach(interceptor => {
			if(!interceptor.match(url)) return

			url = interceptor.transform(url)
		})

		// Include credentials for same-origin tile requests (required for auth)
		if (url.includes(window.location.origin) || url.startsWith('/')) {
			return { url, credentials: 'include' }
		}
		return { url }
    },
	attributionControl: false,
})

function ensureStreetsSource(map) {
	const source_id = "mbx-streets";
	if (!map.getSource(source_id)) {
		map.addSource(source_id, { type: "vector", url: "mapbox://mapbox.mapbox-streets-v8" });
	}
	return source_id;
}
  
function addOrUpdatePlaceLabels(map) {
	const LAYER_ID = "town-city-labels";
	const source_id = ensureStreetsSource(map);

	if (!map.getLayer(LAYER_ID)) {
		map.addLayer({
			id: LAYER_ID,
			type: "symbol",
			source: source_id,
			"source-layer": "place_label",
			minzoom: 3,
			// relaxed so you SEE names (tighten later if you want)
			filter: ["has", "name"],
			layout: {
				"text-field": ["coalesce", ["get", "name_en"], ["get", "name"]],
				// these fonts rendered for you previously
				"text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
				"text-allow-overlap": false,
				"symbol-placement": "point",
				"symbol-sort-key": ["coalesce", ["get", "rank"], 999],
				"text-size": ["interpolate", ["exponential", 1.2], ["zoom"], 5, 9, 10, 14],
			},
			paint: {
				"text-color": "#ffffff",
				"text-halo-color": "#020318",
				"text-halo-width": 2,
				"text-halo-blur": 0.5,
			},
		});
	}
}

function addStateBoundaries(map) {
	const LAYER_ID = "fl-state-boundaries";
	const source_id = ensureStreetsSource(map);

	if (map.getLayer(LAYER_ID)) return;

	const layer = {
		id: LAYER_ID,
		type: "line",
		source: source_id,
		"source-layer": "admin",
		filter: ["==", ["get", "admin_level"], 1],
		layout: {
			"line-join": "round",
			"line-cap": "round",
		},
		paint: {
			"line-color": "#f3f5ff",
			"line-opacity": [
				"interpolate",
				["linear"],
				["zoom"],
				3,
				0.25,
				8,
				0.55,
			],
			"line-width": [
				"interpolate",
				["linear"],
				["zoom"],
				3,
				0.6,
				8,
				1,
			],
		},
	};

	const before_id = firstTextLayerId(map);
	if (before_id) map.addLayer(layer, before_id);
	else map.addLayer(layer);
}

function add3DBuildings(map) {
	const LAYER_ID = "fl-3d-buildings";

	if (map.getLayer(LAYER_ID)) return;

	let sourceId = "composite";
	if (!map.getSource("composite")) {
		sourceId = "mbx-streets";
		if (!map.getSource(sourceId)) {
			map.addSource(sourceId, { type: "vector", url: "mapbox://mapbox.mapbox-streets-v8" });
		}
	}

	map.addLayer({
		id: LAYER_ID,
		type: "fill-extrusion",
		source: sourceId,
		"source-layer": "building",
		minzoom: 15,
		filter: ["any", ["==", ["get", "extrude"], "true"], ["has", "height"], ["has", "min_height"]],
		paint: {
			"fill-extrusion-color": "#2A2B3B",
			"fill-extrusion-opacity": 0.8,
			"fill-extrusion-height": [
				"interpolate",
				["linear"],
				["zoom"],
				15,
				["coalesce", ["get", "min_height"], 0],
				15.05,
				["coalesce", ["get", "height"], 20],
			],
			"fill-extrusion-base": ["coalesce", ["get", "min_height"], 0],
		},
	});

	map.moveLayer(LAYER_ID);
}

function addSkyLayer(map) {
	const LAYER_ID = "fl-sky";

	if (map.getLayer(LAYER_ID)) return;

	map.addLayer({
		id: LAYER_ID,
		type: "sky",
		paint: {
			"sky-type": "atmosphere",
			"sky-atmosphere-color": "#18234a",
			"sky-atmosphere-halo-color": "#4a5688",
			"sky-atmosphere-sun-intensity": 4,
			"sky-opacity": 1,
		},
	});
}

const FLOWLABS_FOG = {
	range: [0.6, 6],
	color: "rgba(8, 12, 30, 0.82)",
	"high-color": "#263363",
	"space-color": "#030514",
	"horizon-blend": 0.12,
	"star-intensity": 0,
};

function setAtmosphere(map) {
	if (!map.setFog) return;

	const current_fog = map.getFog?.();
	const current_fog_matches = current_fog && Object.keys(FLOWLABS_FOG).every(key => {
		return JSON.stringify(current_fog[key]) === JSON.stringify(FLOWLABS_FOG[key]);
	});

	if (!current_fog_matches) {
		map.setFog(FLOWLABS_FOG);
	}
}


function setPaintIfChanged(map, layer_id, prop, value) {
	const current_paint_property = map.getPaintProperty(layer_id, prop);
	if (JSON.stringify(current_paint_property) !== JSON.stringify(value)) {
		map.setPaintProperty(layer_id, prop, value);
	}
}

function forceWhiteLabels(map) {
	const style = map.getStyle();
	if (!style || !style.layers) return;
	for (const layer of style.layers) {
		if (layer.type !== "symbol" || !layer.layout || !layer.layout["text-field"]) continue;
		if (layer.metadata?.skip_force_white_labels) continue;
		if (!map.getLayer(layer.id)) continue;
		setPaintIfChanged(map, layer.id, "text-color", "#ffffff");
		setPaintIfChanged(map, layer.id, "text-halo-color", "#020318");
		setPaintIfChanged(map, layer.id, "text-halo-width", 3);
		setPaintIfChanged(map, layer.id, "text-halo-blur", 0.5);
	}
}

let patch_scheduled = false;
function schedulePatch() {
	if (patch_scheduled) return;
	patch_scheduled = true;
	map.once("idle", () => {
		addOrUpdatePlaceLabels(map);
		addStateBoundaries(map);
		add3DBuildings(map);
		addSkyLayer(map);
		setAtmosphere(map);
		forceWhiteLabels(map);
		patch_scheduled = false;
	});
}

let map_loaded = false

map.once('load', e => {
	schedulePatch();
	map_loaded = true
	m.redraw()
})

map.on('styledata', schedulePatch);

map.setTransformRequest &&
map.setTransformRequest((url, resourceType) => {
	let out = url;
	for (const interceptor of Object.values(request_interceptors)) {
		if (interceptor.match(out)) out = interceptor.transform(out);
	}
	return out !== url ? { url: out } : undefined;
});



// The actual mithril component to attach the map into the mithril and into the document flow.
const MapboxComponent = {}

MapboxComponent.oncreate = vnode => {

	vnode.dom.appendChild(map_element)
	// Once attached resize the map canvas.
	map.resize()
}

MapboxComponent.view = vnode => {
    return m('div.map')
}







/********************************************************************************************************************************
 * redraw:
 * A redraw function that improves the performance of panning and zooming, by deferring redraws.
/********************************************************************************************************************************/

// Assuming `map` is your Mapbox GL JS map instance

let hasInstantiated = false
let isMapInteracting = false; // Flag to track whether the map is being panned or zoomed
let pendingRedraw = false;    // Flag to track whether a redraw has been requested during interaction
let userInteractionVersion = 0;

// Set up event listeners to manage interaction state and conditional redraws
function setupMapInteractionListeners() {
	hasInstantiated = true
    // When the map starts moving or zooming, set the interaction flag to true
    map.on('movestart', event => {
        isMapInteracting = true;
        if (event?.originalEvent) userInteractionVersion += 1;
    });
    map.on('zoomstart', event => {
        isMapInteracting = true;
        if (event?.originalEvent) userInteractionVersion += 1;
    });

    // When the map finishes moving or zooming, check if a redraw is pending
    map.on('moveend', handleInteractionEnd);
    map.on('zoomend', handleInteractionEnd);
}

// Handles the end of map interactions
function handleInteractionEnd() {
    isMapInteracting = false; // Reset the interaction flag
    if (pendingRedraw) {      // If a redraw was requested during interaction
        m.redraw();  // Perform the redraw
        pendingRedraw = false; // Reset the pending redraw flag
    }
}


export function isInteracting() {
	return isMapInteracting && hasInstantiated
}

export function getUserInteractionVersion() {
	return userInteractionVersion
}

export function debounceExecute(func) {

    if (isMapInteracting && hasInstantiated) {
        // If the map is currently being interacted with, mark redraw as pending
        pendingRedraw = true;
    } else {
        // If the map is not being interacted with, proceed with redraw
        func();
    }
}

// Function to request a redraw, considering current interaction state
export function redraw() {

   debounceExecute(m.redraw)
}





/********************************************************************************************************************************
 * Mapbox:
 * The root map component that the user interacts with for rendering sources and layers
/********************************************************************************************************************************/

export const Mapbox = {}

Mapbox.oninit = vnode => {
	
	if(!map_loaded) map.once('load', e => {
		setupMapInteractionListeners()
		// m.redraw() 
	})
}

Mapbox.view = vnode => {

    return [
    	m(MapboxComponent),
    	map_loaded && vnode.children,
    ]
}


function firstTextLayerId(map) {
	const layers = (map.getStyle() && map.getStyle().layers) || [];
	const text = layers.find(
	  l => l.type === 'symbol' && l.layout && l.layout['text-field']
	);
	return text && text.id;
  }

  

function findLabelBeforeId(map) {
	const layers = (map.getStyle() && map.getStyle().layers) || [];
	layers.forEach(l => {
		if (l.type === 'symbol' && l.layout && l.layout['text-field']) {
		  console.log(l.id); // check which symbol layers carry text
		}
	})
	// Prefer a road/street/highway label if we can spot one
	const roadish = layers.find(l =>
	  l.type === 'symbol' &&
	  l.layout && l.layout['text-field'] &&
	  /road|street|highway|motorway|transport/i.test(l.id)
	);
	if (roadish) return roadish.id;
  
	// Fallback: any symbol layer that has a text-field (keeps lines under all labels)
	const anyText = layers.find(l =>
	  l.type === 'symbol' &&
	  l.layout && l.layout['text-field']
	);
	return anyText ? anyText.id : undefined;
  }





/********************************************************************************************************************************
 * Source:
 * Renders and manages the lifecycle of sources.  Registers child layers so that the source can be recreated and removed.
/********************************************************************************************************************************/

export const Source = {}

Source.oninit = vnode => {

	vnode.state.layers = {}

	vnode.state.source_id = vnode.attrs.source_id || uuidv4()

	vnode.state.version_key = vnode.attrs.version_key

	let source
	if(typeof vnode.attrs.source === 'function') source = vnode.attrs.source()
	else source = structuredClone(vnode.attrs.source)


	vnode.state.attribute_source = vnode.attrs.source
	vnode.state.source = source

	// `persist` opts the source out of the standard mount/unmount lifecycle:
	// when the parent component re-mounts (e.g. the user navigates from the
	// roadway view to the subregion view), the underlying Mapbox source is
	// left attached so its tile cache survives. Without this the same MVT
	// tiles get re-fetched on every navigation and the layers visibly
	// disappear until tiles come back.
	if (vnode.attrs.persist && map.getSource(vnode.state.source_id)) {
		// Sync the live Mapbox source to the new attrs spec, but only for
		// fields that actually diverged from the last spec we applied. The
		// previous unconditional setTiles/setUrl/setData calls invalidated
		// Mapbox's tile cache on every reuse, defeating the persist cache.
		const existing_source = map.getSource(vnode.state.source_id)
		const previous_spec = last_applied_source_specs.get(vnode.state.source_id)
		if ((source.type === 'vector' || source.type === 'raster') && existing_source) {
			if (Array.isArray(source.tiles) && typeof existing_source.setTiles === 'function') {
				if(!previous_spec || !arraysShallowEqual(previous_spec.tiles, source.tiles)){
					existing_source.setTiles(source.tiles)
				}
			}
			if (source.url && typeof existing_source.setUrl === 'function') {
				if(!previous_spec || previous_spec.url !== source.url){
					existing_source.setUrl(source.url)
				}
			}
		}
		else if (source.type === 'geojson' && existing_source && source.data && typeof existing_source.setData === 'function') {
			if(!previous_spec || previous_spec.data !== source.data){
				existing_source.setData(source.data)
			}
		}
		last_applied_source_specs.set(vnode.state.source_id, source)

		if(vnode.attrs.intercept) request_interceptors[vnode.state.source_id] = vnode.attrs.intercept
		else delete request_interceptors[vnode.state.source_id]
	}
	else {
		try {
			map.addSource(vnode.state.source_id, vnode.state.source)
			last_applied_source_specs.set(vnode.state.source_id, vnode.state.source)
			if(vnode.attrs.intercept) request_interceptors[vnode.state.source_id] = vnode.attrs.intercept
			else delete request_interceptors[vnode.state.source_id]
		}
		catch(e){
			console.error('Error with Source: ', vnode.state.source)
			throw e
		}
	}


	vnode.state.diffSource = async attrubute_source => {

		// const t0 = Date.now()

		let updated_source
		if(typeof attrubute_source === 'function') updated_source = attrubute_source()

		else updated_source = attrubute_source

		if(vnode.state.source === updated_source) return
	
		// const t1 = Date.now()

		const old_source = vnode.state.source
		vnode.state.attribute_source = vnode.attrs.source
		const source = vnode.state.source = updated_source


		// let t2
		
		// Short Circuit and use Mapbox C++ diff built into setData, roughly 3-6x faster
		if(source.type === 'geojson' && source.type === old_source.type){
			source?.data && map.getSource(vnode.state.source_id)?.setData(source.data)
			// t2 = Date.now()
		}
		else {

			let diff_object = {}
			try{
				// Offload Manual diff to worker thread
				diff_object = await diff_worker.calcDiff(old_source, source) //diff(vnode.state.source, updated_source)
			}
			catch(e){
				console.error(e)
			}

			// t2 = Date.now()

			const recreateSource = () => {
				Object.keys(vnode.state.layers).forEach(layer_id => map.removeLayer(layer_id))
				map.removeSource(vnode.state.source_id)
				map.addSource(vnode.state.source_id, vnode.state.source)
				Object.values(vnode.state.layers).forEach(layer => {
					map.addLayer(layer)
					//TODO-START: This is a hack to get the background layer to render on top of the poi-label layer.  This should be done in a more generic way.
					if (layer.is_background) map.moveLayer(layer.id, 'poi-label')
					//TODO-END: This is a hack to get the background layer to render on top of the poi-label layer.  This should be done in a more generic way.
				}
					)
			}

			// Diffing conditional to handle different update conditions, or full removal and recreation if no specific
			// update condition exists for the diff case.

			if(Object.keys(diff_object).length === 0){
				// Do nothing
			}
			else if(diff_object.type){
				recreateSource()
			}
			else if(source.type === 'canvas' && diff_object.coordinates){
				map.getSource(vnode.state.source_id).setCoordinates(source.coordinates)
			}
			else if(source.type === 'geojson' && diff_object.data){
				map.getSource(vnode.state.source_id)?.setData(source.data)
			}
			else if(source.type === 'image' && ( diff_object.url || diff_object.coordinates )){
				map.getSource(vnode.state.source_id).updateImage({
					coordinates: source.coordinates,
					url: source.url,
				})
			}
			else if(source.type === 'vector' || source.type === 'raster'){
				if(diff_object.url ){
					map.getSource(vnode.state.source_id).setUrl(source.url)
				}
				if(diff_object.tiles ){
					map.getSource(vnode.state.source_id).setTiles(source.tiles)
				}
			}
			else if(source.type === 'video' && diff_object.coordinates){
				map.getSource(vnode.state.source_id).setCoordinates(source.coordinates)
			}
			else {
				recreateSource()
			}
		}


		if(vnode.attrs.intercept) request_interceptors[vnode.state.source_id] = vnode.attrs.intercept
		else delete request_interceptors[vnode.state.source_id]

		last_applied_source_specs.set(vnode.state.source_id, source)


		// const t3 = Date.now()

		// console.log('SOURCE GENERATION:', t1-t0, '\nSOURCE DIFFING:', t2-t1, '\nSOURCE UPDATING:', t3-t2, source)

	}

	vnode.redraw = attribute_source => {
		if(!attribute_source) attribute_source = vnode.state.attribute_source
		vnode.state.diffSource(attribute_source)
	}

}

Source.onbeforeupdate = vnode => {

	if(vnode.attrs.redraw === false) return

	if (vnode.state.current_version && vnode.state.current_version === vnode.attrs.current_version) return;
	
	vnode.state.current_version = vnode.attrs.current_version;
	
	vnode.state.diffSource(vnode.attrs.source)
	
}

// On remove has access to children. Mithril deletes components from the top down for performance reasons
// so the source must have access to the child layers to delete them prior to deleting itself.
Source.onremove = vnode => {

	// `persist`: leave the source (and its tile cache) attached to Mapbox so
	// a subsequent mount can reuse it without refetching. Child layers will
	// still be removed by their own Layer.onremove handlers.
	if (vnode.attrs.persist) return

	// Remove child layers from the map
	Object.keys(vnode.state.layers).forEach(layer_id => {
		if(map.getLayer(layer_id)) map.removeLayer(layer_id)
	})

	map.getStyle().layers.forEach(layer => {
		if(layer.source === vnode.state.source_id && map.getLayer(layer.id)) map.removeLayer(layer.id)
	})

	// Remove the source itself from the map
	map.removeSource(vnode.state.source_id)

	delete request_interceptors[vnode.state.source_id]
	last_applied_source_specs.delete(vnode.state.source_id)
}


const recursiveLayerSearch = (vnodes, layers) => {
	layers = layers || []

	vnodes.forEach(vnode => {
		// console.log('VNODE', vnode)

		if (!vnode) return

		if(vnode.attrs.source) return // Break loop on source to allow the source to start it's own tree
		
		if(vnode.attrs.layer) layers.push(vnode)
		
		if(vnode.children && vnode.children.length > 0){
			layers = layers.concat(recursiveLayerSearch(vnode.children))
		}
	})

	return layers
}

Source.view = vnode => {

	recursiveLayerSearch(vnode.children).forEach(layer => {
		
		layer.parent_source_id = vnode.state.source_id
		
		// Provided functions on the children to register and deregister sources
		layer.register = layer => {
			vnode.state.layers[layer.id] = layer
		}
		layer.deregister = layer => {
			vnode.state.layers[layer.id] = null
			delete vnode.state.layers[layer.id]
		}
	})

	return vnode.children
}









/********************************************************************************************************************************
 * Layer:
 * Renders and manages the lifecycle of layers.  Also registers them with the parent source, and diffs and updates changes.
/********************************************************************************************************************************/

export const Layer = {}

Layer.oninit = vnode => {

	if(vnode.children.length > 0) throw new Error('Layer component does not accept children.')
	vnode.state.layer_id = vnode.attrs.layer_id || uuidv4()

	let layer
	if(typeof vnode.attrs.layer === 'function') layer = vnode.attrs.layer()
	else layer = structuredClone(vnode.attrs.layer)

	vnode.state.attribute_layer = vnode.attrs.layer
	vnode.state.layer = layer

	vnode.state.layer.id = vnode.state.layer_id
	vnode.state.layer.source = vnode.parent_source_id

	// `persist`: when the layer is already attached from a previous mount
	// cycle (parent component swapped pages but the Mapbox layer should
	// survive), reuse it without removing+re-adding.
	const layer_already_attached = vnode.attrs.persist && map.getLayer(vnode.state.layer_id)

	if (!layer_already_attached) {
		try {
			const beforeId =
			  typeof vnode.attrs.before === 'string'
				? vnode.attrs.before
				: firstTextLayerId(map);

			if (map.getLayer(vnode.state.layer_id)) {
			  map.removeLayer(vnode.state.layer_id);
			}

			if (beforeId && map.getLayer(beforeId)) {
			  map.addLayer(vnode.state.layer, beforeId);
			} else {
			  map.addLayer(vnode.state.layer);
			}
			last_applied_layer_specs.set(vnode.state.layer_id, vnode.state.layer)
		} catch (e) {
			console.error('Error with Layer: ', vnode.state.layer);
			throw e;
		}
	}
	else {
		// Sync the live Mapbox layer to the new spec, but only for paint /
		// layout / filter fields that actually changed since we last synced
		// this layer. Calling setPaintProperty / setLayoutProperty / setFilter
		// unconditionally on every reuse forces redraws and defeats persist.
		try {
			const layer_id = vnode.state.layer_id
			const new_layer = vnode.state.layer
			const previous_spec = last_applied_layer_specs.get(layer_id)
			const layer_diff = previous_spec ? diff(previous_spec, new_layer) : new_layer

			if (layer_diff.paint) {
				Object.keys(layer_diff.paint).forEach(prop => {
					try { map.setPaintProperty(layer_id, prop, new_layer.paint[prop]) } catch (e) { /* property may not apply to this layer type */ }
				})
			}
			if (layer_diff.layout) {
				Object.keys(layer_diff.layout).forEach(prop => {
					try { map.setLayoutProperty(layer_id, prop, new_layer.layout[prop]) } catch (e) { /* same */ }
				})
			}
			if (layer_diff.filter !== undefined) {
				try { map.setFilter(layer_id, new_layer.filter) } catch (e) { /* same */ }
			}
			last_applied_layer_specs.set(layer_id, new_layer)
		} catch (e) {
			console.error('Error syncing persisted Layer: ', vnode.state.layer)
		}
	}
	  



	// Map event handlers
	vnode.state.event_handlers = {}

	// Extract the events from the attributes
	Object.keys(vnode.attrs).forEach(key => {
		if(key.startsWith('on') && !isLifecycleMethod(key)){
			const event_key = key.slice(2)
			vnode.state.event_handlers[event_key] = e => {
				vnode.attrs[key](e)
				// if(e.redraw !== false) m.redraw()
			}
		}
	})

	// Attach the events to the map
	Object.keys(vnode.state.event_handlers).forEach(key => {
		map.on(key, vnode.state.layer_id, vnode.state.event_handlers[key])
	})


	// Register this layer with the parent source so it can do cleanup in the top down mithril rendering order
	vnode.register(vnode.state.layer)


	vnode.state.diffSource = attribute_layer => {
		
		vnode.attrs.layer.id = vnode.state.layer_id
		vnode.attrs.layer.source = vnode.parent_source_id

		let updated_layer
		if(typeof attribute_layer === 'function') updated_layer = attribute_layer()
		else updated_layer = structuredClone(attribute_layer)

		let diff_object = {}
		try{
			diff_object = diff(vnode.state.layer, updated_layer)
		}
		catch(e){
			console.error(e)
		}

		vnode.state.attribute_layer = attribute_layer
		const layer = vnode.state.layer = updated_layer

		const recreateLayer = () => {
			map.removeLayer(vnode.state.layer_id);
			const beforeId = (typeof vnode.attrs.before === 'string')
			  ? vnode.attrs.before
			  : findLabelBeforeId(map);
		  
			if (beforeId) {
			  map.addLayer(vnode.state.layer, beforeId);
			} else {
			  map.addLayer(vnode.state.layer);
			}
		  };
		  


		// Diffing conditional to handle different update conditions, or full removal and recreation if no specific
		// update condition exists for the diff case.
		if(diff_object.type || diff_object.renderingMode || diff_object['source-layer'] || diff_object.metadata){
			recreateLayer()
		}
		else {

			if(diff_object.paint){
				Object.keys(diff_object.paint).forEach(prop => {
					try { map.setPaintProperty(vnode.state.layer_id, prop, layer.paint[prop]) }
					catch(e) { console.error('Error setting paint property: ', vnode.state.layer_id, prop, e) }
				})
			}

			if(diff_object.layout){
				Object.keys(diff_object.layout).forEach(prop => {
					try { map.setLayoutProperty(vnode.state.layer_id, prop, layer.layout[prop]) }
					catch(e) { console.error('Error setting layout property: ', vnode.state.layer_id, prop, e) }
				})
			}

			if(diff_object.filter){
				map.setFilter(vnode.state.layer_id, layer.filter)
			}

			if(diff_object.minzoom || diff_object.maxzoom){
				map.setLayerZoomRange(vnode.state.layer_id, layer.minzoom, layer.maxzoom);
			}
		}

		last_applied_layer_specs.set(vnode.state.layer_id, layer)
	}


	vnode.redraw = attribute_layer => {
		if(!attribute_layer) attribute_layer = vnode.state.attribute_layer
		vnode.state.diffSource(attribute_layer)
	}

}

Layer.onbeforeupdate = vnode => {

	if(vnode.attrs.redraw === false) return

	vnode.state.diffSource(vnode.attrs.layer)

	// Dynamic Event Handler Management
	// WARNING:
	// This initial implementation (based only on existence) was done as a work around for stale values in event handler closures.
	// Proper switching to fresh closures is TODO.
	// Extract the events from the attributes
	Object.keys(vnode.attrs).forEach(key => {
		if(key.startsWith('on') && !isLifecycleMethod(key)){
			const event_key = key.slice(2)

			if (vnode.attrs[key] == null && vnode.state.event_handlers[event_key] != null) {
				map.off(event_key, vnode.state.layer_id, vnode.state.event_handlers[event_key])
				delete vnode.state.event_handlers[event_key]
			}
			else if (vnode.attrs[key] != null && vnode.state.event_handlers[event_key] == null) {
				vnode.state.event_handlers[event_key] = e => {
					vnode.attrs[key](e)
					// if(e.redraw !== false) m.redraw()
				}
				map.on(event_key, vnode.state.layer_id, vnode.state.event_handlers[event_key])
			}
		}
	})

}

Layer.onremove = vnode => {


	// Remove event handlers from map (always — the new mount installs its own)
	Object.keys(vnode.state.event_handlers).forEach(key => {
		// console.log('deregistered ', key)
		map.off(key, vnode.state.layer_id, vnode.state.event_handlers[key])
	})

	// `persist`: leave the Mapbox layer attached so the next mount cycle can
	// reuse it (paired with the matching check in oninit). Avoids the visible
	// "edges blink" when the parent component re-mounts on navigation.
	if (vnode.attrs.persist) return

	// Remove Layer
	if(map.getLayer(vnode.state.layer_id)){
		vnode.deregister(vnode.state.layer_id)
		map.removeLayer(vnode.state.layer_id)
	}
	last_applied_layer_specs.delete(vnode.state.layer_id)
}

Layer.view = vnode => {
	return null
}












function isLifecycleMethod(attr) {
	return attr === "oninit" || attr === "oncreate" || attr === "onupdate" || attr === "onremove" || attr === "onbeforeremove" || attr === "onbeforeupdate"
}
